import cx from 'classnames';
import { intcomma } from 'journalize';
import { Component, h } from 'preact';

import OtherCandidate from './OtherCandidate';
import { PreactHTMLConverter } from 'preact-html-converter';

// internal
import {
  sort_candidates_by_party,
  sort_candidates_by_votes,
  alert_conditionals,
  alert_winners,
  text_conditionals,
  text_conditional_winner_zero,
  get_directory_slug,
  getDistrictNum
} from '../election-utils';
import sumValues from '../utils/sum-values';
import formatPercent from '../utils/format-percent';
import winnerClass from '../utils/winner-class';
import isCompetitive from '../utils/competitive';
import text from 'Data/text.json';

import { isCanvass, visibleCandidatesCount } from '../endpoints';

const PLAIN_SPECIAL_ELECTIONS: string[] = [];

// Spelled out in full so PurgeCSS (config/tasks/unused-css.js) keeps them.
// Same party colors as the PartyPill circles.
const WINNER_BOX_CLASSES: { [prefix: string]: string } = {
  rep: 'winner-box--rep',
  dem: 'winner-box--dem',
  lib: 'winner-box--lib',
  ind: 'winner-box--ind',
  gre: 'winner-box--gre',
};



function withCandidateKey(row: any, candidate: any) {
  if (row && row.key == null && candidate.id != null) {
    row.key = candidate.id;
    row.attributes = { ...row.attributes, key: candidate.id };
  }
  return row;
}

// From the text doc's [+competitive_note]; may contain a link
const COMPETITIVE_NOTE = (text.competitive_note || [])
  .map((block) => block.value)
  .join(' ');

type Props = {
  contest: { [key: string]: any };
  columnCount: number;
  contestTitle: string;
  includeCountyRundown?: boolean;
  isShowcaseRace?: boolean;
  renderParty: any;
  renderCandidate: any;
  candidatesRender: any;
  showRedrawn?: boolean;
  embedType: string;
  broadcastHeaderImage: string;
  activeContestId: string;
  tableHeader: JSX.Element;
  annotations: { [key: string]: any };
  notes: { [key: string]: any };
  isHomepage?: boolean;
  asOf: string;
  source: string;
};

class Contest extends Component<Props> {
  constructor() {
    super();

    this.state = {
      viewAllCandidates: false,
    };
    this.onViewableToggle = this.onViewableToggle.bind(this);
  }

  onViewableToggle() {
    this.setState(prevState => ({
      viewAllCandidates: !prevState.viewAllCandidates, 
    }));
  }
  render() {
    const {
      contest,
      columnCount,
      contestTitle,
      isShowcaseRace,
      renderCandidate,
      annotations,
      notes,
      tableHeader,
      isHomepage,
      asOf,
      source: feedSource,
      embedType,
      candidatesRender,
      showRedrawn,
    } = this.props;

    
    const source = feedSource || 'Associated Press';

    const { viewAllCandidates } = this.state;

    const converter = PreactHTMLConverter();

    const shouldDisplayResult = contest.contest_result !== 'not called';

    let directory_slug, district_num;

    if (contest) {
      directory_slug = get_directory_slug(contest.office);
      district_num = getDistrictNum(contest.division_type, contest.office_division)
    }

    const totalVotes = sumValues(contest.candidacies, 'votes');

    let candidates;

    candidates =
      totalVotes == 0
        ? sort_candidates_by_party(contest.candidacies, 'table')
        : sort_candidates_by_votes(contest.candidacies);

    const uncontested = candidates.length == 1;

    const pct_votes = source == 'Decision Desk HQ' ? formatPercent(
      totalVotes,
      contest.estimated_votes_mid
    )  : contest.eevp

    const pct_votes_turnout =
      pct_votes < 100
        ? `An estimated ${pct_votes}% of votes have been counted, according to the ${source}.`
        : `An estimated >99% of votes have been counted, according to the ${source}.`;


    const pct_votes_turnout_zero_winner = 
    pct_votes == 0 && shouldDisplayResult 
    ? ``
    : pct_votes_turnout;

    const pct_votes_turnout_w_uncontested =
        (uncontested == true)
           ? text_conditionals(pct_votes, contest.candidacies.length) :
           pct_votes_turnout_zero_winner

    const pct_votes_explain = !shouldDisplayResult && !uncontested
      ? text_conditionals(pct_votes, contest.candidacies.length)
      : text_conditional_winner_zero(pct_votes, contest.candidacies.length);

    let firstShowcaseRace;

    let embedTypeUsable = 'none';

    if (typeof embedType !== 'undefined') {
      embedTypeUsable = embedType;
    }


    const showcaseClass = cx({
      seat__races: !isShowcaseRace,
      seat__highlight_races: isShowcaseRace,
    });


    const hasSpecialStyling =
      contest.election_type == 'special' &&
      !PLAIN_SPECIAL_ELECTIONS.includes(contest.office_slug);

    let raceClass = cx('seat__race', {
      'special-election': hasSpecialStyling,
      'general-seat-before-special': (contest.office_slug == 'us-house-district-18') || (contest.office_slug == 'texas-house-district-56'),
    })

    let seatClass = cx('seat',  {
      'special-election__grid': hasSpecialStyling,
    })

    const klaxon_sos = 'Race has been declared'; // if we switch sources to TX SOS and someone else has declared the winner

    const klaxon = shouldDisplayResult
      ? source == 'Decision Desk HQ' || source == 'Associated Press'
        ? alert_winners(contest.contest_result, contest.office)
        : klaxon_sos
      : alert_conditionals(pct_votes, contest.candidacies.length);

    const checkmark = '\u2713';

    let hasTurnoutmodel = (source == 'Decision Desk HQ' || source == 'Associated Press')? true : false;

    // Color the checkmark by the winner's party
    const winner = candidates.find((candidate: any) => candidate.is_winner);
    const winnerColorClass =
      shouldDisplayResult && hasTurnoutmodel && winner
        ? winnerClass(winner.party, contest.contest_result)
        : '';
    const winnerCheckClass = cx('check', winnerColorClass);

    // Box above the table once the AP calls the race, in the winner's pill color.
    // Uncontested races get it too, for their only candidate
    const isUncontestedResult =
      contest.contest_result == 'uncontested' && uncontested;
    const boxWinner = isUncontestedResult ? candidates[0] : winner;
    const showWinnerBox =
      (contest.contest_result == 'winner' &&
        source == 'Associated Press' &&
        winner) ||
      isUncontestedResult;
    const winnerCustomColor =
      showWinnerBox && Array.isArray(candidatesRender)
        ? (candidatesRender.find((c: any) => c.name == boxWinner.name) || {}).color
        : undefined;
    const winnerBoxClass = cx(
      'winner-box',
      showWinnerBox &&
        !winnerCustomColor &&
        WINNER_BOX_CLASSES[(boxWinner.party || '').slice(0, 3).toLowerCase()]
    );


    const contestMetricsClass = cx('contest__metrics', {
      homepage__metrics: isHomepage,
      'non-homepage__metrics': !isHomepage,
    });


    const precinctsClass = cx('contest__precincts');

    let ManualoverrideSource;


    ManualoverrideSource = '';

    const IsAnnotations =
      annotations && annotations.length > 0 && annotations[0].value != ''
        ? true
        : false;

    const IsNoted = notes && notes.length > 0 ? true : false;

    return (
      <div class={seatClass}>
        <div class={showcaseClass}>
          <div class={raceClass}>
          {!isShowcaseRace && (
              <h3 class="seat__title">
                <span>{contest.election_type == 'special' && (
                <div class="special-election__title">Special Election</div>
              )}{contestTitle}</span>
                {directory_slug[0] != null && (
              <a class="t-links-unset"
                target="_blank"
                href={`https://directory.texastribune.org/districts/${directory_slug[0]['directory-slug']}/${contest['office_division']}/`}
              >
                <span class="icon-div" title="Click to learn more about this district">
                  <svg class="icon icon-info">
                    <use xlinkHref="#icon-info"></use>
                  </svg>
                </span>
              </a>
            )}
                {/* Texas' congressional map was redrawn mid-decade in 2025.
                Only shown where asked for (the U.S. House section and the
                personalized results). */}
                {showRedrawn && contest.office === 'U.S. House' && (
                  <span class="term-limit seat__redrawn">Redrawn in <a href="https://www.texastribune.org/2025/08/18/texas-redistricting-maps-charts-analysis/" target="_blank" rel="noopener noreferrer">2025</a></span>
                )}
              </h3>
            )}
            <div class="race">
              {embedType == 'homepage' && (
                <span>
                  <div class="office__title">{contestTitle}</div>
                </span>
              )}
              {/* {embedType == 'internal' && (
                <span>
                  <h2 class="office__title">{contestTitle}</h2>
                </span>
              )} */}

              <div class={contestMetricsClass}>
                {/* Once the AP calls the race, the winner box replaces the
                    "projected a winner" line */}
                {showWinnerBox && (
                  <div
                    class={winnerBoxClass}
                    style={
                      winnerCustomColor
                        ? { 'background-color': winnerCustomColor }
                        : undefined
                    }
                  >
                    <span class="winner-box__check" aria-hidden="true">
                      {checkmark}
                    </span>
                    <span class="winner-box__text">{boxWinner.name}</span>
                    <span class="winner-box__source">
                      {isUncontestedResult ? 'Uncontested' : 'AP projection'}
                    </span>
                  </div>
                )}
                {!showWinnerBox && isCanvass == false && (
                  <h4 class="klaxon__text">
                    {shouldDisplayResult && (source == 'Decision Desk HQ' || source == 'Associated Press') ? (
                      <span class={winnerCheckClass}>{checkmark}</span>
                    ) : (
                      ''
                    )}
                    {klaxon}
                  </h4>
                )}
                {ManualoverrideSource == '' && isCanvass == false && (
                  <p class={precinctsClass}>
                    {source == 'Texas Secretary of State'
                      ? `${intcomma(
                          contest.pollinglocationsreporting
                        )} of ${intcomma(
                          contest.total_pollinglocations
                        )} polling locations reporting (${contest.pollinglocations_reporting_percent.toFixed(
                          1
                        )}%).`
                      : pct_votes_turnout_w_uncontested}{' '}
                    {converter.convert(pct_votes_explain)}
                  </p>
                )}
                {
                  (ManualoverrideSource =! '' &&
                    source != 'Texas Secretary of State' &&
                    source != 'Decision Desk HQ' &&
                    source != 'Associated Press' &&
                    isCanvass == false && (
                      <p class={precinctsClass}>
                        {`${intcomma(
                          contest.pollinglocationsreporting
                        )} of ${intcomma(
                          contest.total_pollinglocations
                        )} polling locations reporting (${contest.pollinglocations_reporting_percent.toFixed(
                          1
                        )}%).`}

                        {/* <span class="source">{` Source: ${ManualoverrideSource}`}</span> */}
                      </p>
                    ))
                }
              </div>

              <table class="race__candidates">
                {tableHeader}
                <tbody>
                  {isShowcaseRace &&
                    candidates.map((candidate: any, idx: any) =>
                      withCandidateKey(
                        renderCandidate(
                          candidate,
                          idx,
                          candidatesRender,
                          viewAllCandidates,
                          { totalVotes }
                        ),
                        candidate
                      )
                    )}

                  {!isShowcaseRace &&
                    candidates.map((candidate: any, idx: any) =>
                      withCandidateKey(
                        renderCandidate(candidate, idx, viewAllCandidates, {
                          totalVotes,
                        }),
                        candidate
                      )
                    )}
                  {candidates.length > visibleCandidatesCount && (
                    <OtherCandidate
                      isViewable={!viewAllCandidates}
                      candidateCount={candidates.length}
                      embedType={embedType}
                      onClick={() => {
                        this.onViewableToggle();
                      }}
                    />
                  )}
                </tbody>
                {candidates.length > visibleCandidatesCount &&
                  viewAllCandidates && (
                    <tr class="candidate race__candidate-row">
                      <td
                        class="candidate__cell candidate__party t-align-center"
                        onClick={() => {
                          this.onViewableToggle();
                        }}
                      >
                        <svg class="icon icon-angle-up">
                          <use xlinkHref="#icon-angle-up" />
                        </svg>
                      </td>
                      <th class="candidate__cell candidate__name">
                        <button
                          class={embedType}
                          onClick={() => {
                            this.onViewableToggle();
                          }}
                        >
                          Show fewer ({visibleCandidatesCount})
                        </button>
                      </th>
                      <th class="candidate__cell candidate__votes"></th>
                      <th class="candidate__cell candidate__pct"></th>
                    </tr>
                  )}
              </table>
              {isCompetitive(contest) && (
                <div class="competitive-note">
                  <svg class="icon competitive-note__icon" aria-hidden="true">
                    <use xlinkHref="#icon-fire" />
                  </svg>
                  <p class="competitive-note__text">
                    {converter.convert(COMPETITIVE_NOTE)}
                  </p>
                </div>
              )}
              {IsNoted == true && (
                <div class="anno-text notes-annotations">
                  <span class="is-sr-only">Note:</span>
                  <svg class="icon note-icon anno-div" aria-hidden="true">
                    <use xlinkHref="#note-icon" />
                  </svg>
                  <p class="note__text">{converter.convert(notes[0].value)}</p>
                </div>
              )}
              {IsAnnotations == true && (
                <div class="anno-text notes-annotations t-wrap-balance">
                  <span class="is-sr-only">Note:</span>
                  <svg class="icon note-icon anno-div" aria-hidden="true">
                    <use xlinkHref="#note-icon" />
                  </svg>

                  <p class="annotations t-links-underlined">
                    <span class="font-weight-bold">Noteworthy: </span>
                    {converter.convert(annotations[0].value)}
                  </p>
                </div>
              )}

              {/* Source on every card; last updated only on homepage and internal embeds */}

              <div class="below-races">
                <div class="source">{` Source: ${source}`}</div>
                {(embedType == 'homepage' || embedType == 'internal') && (
                  <div class="countdown">
                    <span class="counter-homepage">LAST UPDATED {asOf}</span>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>
    );
  }
}

Contest.defaultProps = {
  isShowcaseRace: false,
  includeCountyRundown: false,
};

export default Contest;